import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Pencil, CheckCircle2, Shield, FileText, Lock, ShieldCheck } from "lucide-react";
import HeroAnimation from "@/components/HeroAnimation/HeroAnimation";
import ROICalculator from "@/components/ROICalculator/ROICalculator";
import ScrollReveal from "@/components/ScrollReveal/ScrollReveal";
import BillingToggle from "@/components/BillingToggle/BillingToggle";
import CountUp from "@/components/CountUp/CountUp";
import { CONTACTS } from "@/constants/contacts";

const PRICING = {
  solo: {
    monthly: 129,
    annual: 103,
    annualNoteMonthly: "ou R$103/mês no plano anual",
    annualBilling: "Cobrado como R$1.236/ano — economia de R$312",
    savingsBadge: "ECONOMIZE R$312/ANO",
  },
  clinic: {
    monthly: 99,
    annual: 79,
    annualBilling: "Cobrado como R$948/assento/ano — economia de R$240/assento",
  },
};

export default function HomePage() {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const isAnnual = billingPeriod === "annual";

  return (
    <>
      {/* SECTION 1 — HERO */}
      <section className="hero" data-testid="section-hero">
        <span className="hero-bg-blob hero-bg-blob-1" aria-hidden="true" />
        <span className="hero-bg-blob hero-bg-blob-2" aria-hidden="true" />

        <div className="hero-inner">
          <span className="eyebrow" data-testid="hero-eyebrow">
            INTELIGÊNCIA CLÍNICA PARA FISIOTERAPEUTAS
          </span>
          <h1 className="h-1 hero-headline" data-testid="hero-headline">
            O prontuário que se escreve enquanto você cuida.
          </h1>
          <p className="body-lg hero-subheadline" data-testid="hero-subheadline">
            Grave a sessão em voz. A IA estrutura o prontuário e sugere o plano
            terapêutico a partir da sua avaliação. Você revisa, ajusta e aprova.
          </p>

          <HeroAnimation />

          <div className="hero-cta-row">
            <a
              href={CONTACTS.app + "/signup"}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              data-testid="hero-cta-primary"
            >
              Começar grátis
            </a>
            <Link
              to="/como-funciona"
              className="btn btn-ghost"
              data-testid="hero-cta-secondary"
            >
              Ver como funciona
            </Link>
          </div>

          <div className="hero-social-proof" data-testid="hero-social-proof">
            <span>15 profissionais em beta</span>
            <span className="hero-social-proof-sep">·</span>
            <span>PT-BR nativo</span>
          </div>
        </div>
      </section>

      {/* SECTION 2 — PROBLEM */}
      <section className="section section-surface" data-testid="section-problem">
        <div className="container">
          <ScrollReveal>
            <div className="problem-stat">
              <div>
                <span className="problem-stat-num" data-testid="problem-stat-num">
                  45
                </span>
              </div>
              <div className="problem-stat-unit">min/dia</div>
              <p className="problem-stat-caption">
                é o tempo médio perdido por fisioterapeutas brasileiros com
                documentação manual — tempo que poderia ser de atendimento.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="compare-grid">
              <div className="compare-card compare-card-bad" data-testid="compare-without">
                <p className="compare-card-title">Sem Akali AI</p>
                <ul className="compare-list">
                  {[
                    "Prontuários escritos à mão ou no WhatsApp",
                    "Sem padronização entre profissionais",
                    "Risco de não-conformidade COFFITO",
                    "Tempo de documentação rouba tempo de paciente",
                    "Retrabalho em auditorias e perícias",
                  ].map((t) => (
                    <li className="compare-item" key={t}>
                      <span className="compare-icon-bad">×</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="compare-card compare-card-good" data-testid="compare-with">
                <p className="compare-card-title">Com Akali AI</p>
                <ul className="compare-list">
                  {[
                    "Prontuário estruturado em minutos por voz",
                    "Campos COFFITO preenchidos automaticamente",
                    "Revisão humana antes de qualquer salvamento",
                    "Em conformidade com a LGPD por arquitetura",
                    "Exportação em PDF a qualquer momento",
                  ].map((t) => (
                    <li className="compare-item" key={t}>
                      <span className="compare-icon-good">✓</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* SECTION 3 — HOW IT WORKS (abbreviated) */}
      <section className="section section-deep" data-testid="section-how">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">PROCESSO</span>
              <h2 className="h-2">Três etapas. Sem mudar sua rotina.</h2>
            </div>
          </ScrollReveal>

          <div className="steps-row">
            <ScrollReveal delay={0.05} className="step-card glass glass-hover" data-testid="step-1">
              <div className="step-icon">
                <Mic size={22} />
              </div>
              <h3 className="step-title">Grave</h3>
              <p className="step-body">
                Durante ou após a sessão. Fale naturalmente em português —
                gírias clínicas, abreviações, tudo funciona.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.15} className="step-card glass glass-hover" data-testid="step-2">
              <div className="step-icon">
                <Pencil size={22} />
              </div>
              <h3 className="step-title">Revise</h3>
              <p className="step-body">
                A IA estrutura o prontuário com os campos COFFITO. Você lê,
                edita inline se necessário, e tem controle total.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.25} className="step-card glass glass-hover" data-testid="step-3">
              <div className="step-icon">
                <CheckCircle2 size={22} />
              </div>
              <h3 className="step-title">Aprove</h3>
              <p className="step-body">
                Um clique aprova e finaliza, gerando registro de aprovação com
                trilha de auditoria. Prontuário exportável em PDF, conforme
                COFFITO 414/2012.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.3}>
            <div className="compliance-note" data-testid="compliance-note">
              <ShieldCheck size={16} color="#F18859" style={{ flexShrink: 0, marginTop: 2 }} />
              <p className="compliance-note-text">
                A revisão humana não é uma opção — é uma restrição arquitetural.
                Nenhum prontuário é salvo sem aprovação explícita do
                profissional.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.35}>
            <div className="section-cta-row">
              <Link to="/como-funciona" className="link-accent" data-testid="cta-how-full">
                Ver processo completo →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* SECTION 4 — ROI CALCULATOR */}
      <section className="section section-surface" data-testid="section-roi">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">CALCULADORA</span>
              <h2 className="h-2">Quanto tempo você vai recuperar?</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <ROICalculator />
          </ScrollReveal>
        </div>
      </section>

      {/* SECTION 5 — PRICING */}
      <section className="section section-deep" id="planos" data-testid="section-pricing">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">PLANOS</span>
              <h2 className="h-2">Simples. Sem surpresas.</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.05}>
            <BillingToggle value={billingPeriod} onChange={setBillingPeriod} />
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="pricing-row">
              <PricingCard
                tier="Starter"
                price="Grátis"
                period="para sempre"
                features={[
                  { text: "5 prontuários aprovados/mês", enabled: true },
                  { text: "Transcrição PT-BR", enabled: true },
                  { text: "Campos básicos COFFITO", enabled: true },
                  { text: "Exportação em PDF", enabled: true },
                  { text: "Suporte prioritário", enabled: false },
                  { text: "Multi-usuário", enabled: false },
                ]}
                ctaLabel="Criar conta grátis"
                ctaClass="btn btn-ghost btn-full"
                ctaHref={CONTACTS.app + "/signup"}
                testId="pricing-starter"
              />

              <PricingCard
                tier="Solo"
                priceNumeric={isAnnual ? PRICING.solo.annual : PRICING.solo.monthly}
                priceAccent
                period="/mês"
                annualNote={isAnnual ? null : PRICING.solo.annualNoteMonthly}
                annualBilling={isAnnual ? PRICING.solo.annualBilling : null}
                savingsBadge={isAnnual ? PRICING.solo.savingsBadge : null}
                features={[
                  { text: "80 prontuários aprovados/mês", enabled: true },
                  { text: "Transcrição PT-BR ilimitada", enabled: true },
                  { text: "Todos os campos COFFITO 414/2012", enabled: true },
                  { text: "Exportação em PDF com trilha de auditoria", enabled: true },
                  { text: "Histórico completo de sessões", enabled: true },
                  { text: "Suporte prioritário por e-mail", enabled: true },
                  { text: "Multi-usuário", enabled: false },
                ]}
                ctaLabel={isAnnual ? "Assinar Solo Anual" : "Assinar Solo"}
                ctaClass="btn btn-primary btn-full"
                ctaHref={
CONTACTS.app +
                  (isAnnual ? "/signup?plan=solo&billing=annual" : "/signup?plan=solo")
                }
                featured
                testId="pricing-solo"
              />

              <PricingCard
                tier="Clínica"
                priceNumeric={isAnnual ? PRICING.clinic.annual : PRICING.clinic.monthly}
                period="/assento/mês"
                priceNote="mínimo 3 assentos"
                annualBilling={isAnnual ? PRICING.clinic.annualBilling : null}
                features={[
                  { text: "Tudo do Solo por assento", enabled: true },
                  { text: "Dashboard multi-usuário", enabled: true },
                  { text: "Relatórios de produtividade da equipe", enabled: true },
                  { text: "Gestão centralizada de prontuários", enabled: true },
                  { text: "Onboarding dedicado", enabled: true },
                  { text: "Suporte prioritário", enabled: true },
                ]}
                ctaLabel="Falar com a equipe"
                ctaClass="btn btn-ghost btn-full"
                ctaTo="/para-clinicas#contato"
                testId="pricing-clinic"
              />
            </div>
          </ScrollReveal>

          <p className="pricing-disclaimer">
            1 prontuário aprovado = 1 consulta. Rascunhos e gerações não contam.
          </p>
        </div>
      </section>

      {/* SECTION 6 — TRUST */}
      <section className="section-tight section-surface" data-testid="section-trust">
        <div className="container">
          <div className="trust-grid">
            <ScrollReveal delay={0.0} className="trust-pillar">
              <div className="trust-icon">
                <Shield size={22} />
              </div>
              <h3 className="trust-title">LGPD Art. 11</h3>
              <p className="trust-body">
                Dados sensíveis de saúde tratados conforme Art. 11 da LGPD.
                Armazenamento no Brasil. Nenhum dado compartilhado com terceiros
                sem consentimento explícito.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.1} className="trust-pillar">
              <div className="trust-icon">
                <FileText size={22} />
              </div>
              <h3 className="trust-title">COFFITO 414/2012</h3>
              <p className="trust-body">
                Estrutura de prontuário alinhada à Resolução COFFITO 414/2012.
                Campos obrigatórios mapeados. Revisão humana antes de qualquer
                finalização — por design, não por configuração.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="trust-pillar">
              <div className="trust-icon">
                <Lock size={22} />
              </div>
              <h3 className="trust-title">Segurança de ponta a ponta</h3>
              <p className="trust-body">
                Autenticação JWT. Isolamento de dados por usuário. Sem acesso
                cruzado entre profissionais. Logs de auditoria em cada
                aprovação.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 7 — FINAL CTA */}
      <section className="final-cta" data-testid="section-final-cta">
        <ScrollReveal>
          <h2 className="final-cta-headline">Comece hoje. Grátis.</h2>
          <p className="final-cta-sub">
            5 prontuários por mês sem custo. Sem cartão de crédito.
          </p>
          <div className="final-cta-row">
            <a
              href={CONTACTS.app + "/signup"}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-large"
              data-testid="final-cta-primary"
            >
              Criar conta gratuita
            </a>
            <Link
              to="/para-clinicas#contato"
              className="link-accent"
              style={{ color: "var(--color-text-muted)" }}
              data-testid="final-cta-secondary"
            >
              Tem dúvidas? Fale com a equipe →
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </>
  );
}

function PricingCard({
  tier,
  price,
  priceNumeric,
  priceAccent,
  period,
  priceNote,
  annualNote,
  annualBilling,
  savingsBadge,
  features,
  ctaLabel,
  ctaClass,
  ctaHref,
  ctaTo,
  featured,
  testId,
}) {
  const noteKey = savingsBadge ? "savings" : annualBilling ? "billing" : annualNote ? "note" : "empty";
  const noteText = annualBilling || annualNote;

  return (
    <div
      className={`pricing-card ${featured ? "pricing-card-featured" : ""}`}
      data-testid={testId}
    >
      {featured && <span className="pricing-badge">Mais Popular</span>}
      <h3 className="pricing-title">{tier}</h3>

      <AnimatePresence initial={false} mode="popLayout">
        {savingsBadge && (
          <motion.span
            key="savings-badge"
            className="pricing-savings"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            data-testid={`${testId}-savings-badge`}
          >
            {savingsBadge}
          </motion.span>
        )}
      </AnimatePresence>

      <div className="pricing-price-row">
        <span
          className={`pricing-price ${priceAccent ? "pricing-price-accent" : ""}`}
          data-testid={`${testId}-price`}
        >
          {priceNumeric != null ? (
            <>
              R$
              <CountUp value={priceNumeric} duration={0.4} />
            </>
          ) : (
            price
          )}
        </span>
        {period && <span className="pricing-period">{period}</span>}
      </div>

      {priceNote && <p className="pricing-note">{priceNote}</p>}

      <AnimatePresence mode="wait" initial={false}>
        {noteText && (
          <motion.p
            key={noteKey}
            className="pricing-note pricing-note-accent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid={`${testId}-billing-note`}
          >
            {noteText}
          </motion.p>
        )}
      </AnimatePresence>

      <div className="pricing-divider" />

      <ul className="pricing-features">
        {features.map((f) => (
          <li
            key={f.text}
            className={`pricing-feature ${!f.enabled ? "pricing-feature-disabled" : ""}`}
          >
            <span className="pricing-feature-icon">{f.enabled ? "✓" : "—"}</span>
            <span>{f.text}</span>
          </li>
        ))}
      </ul>

      {ctaHref ? (
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          className={ctaClass}
          data-testid={`${testId}-cta`}
        >
          {ctaLabel}
        </a>
      ) : (
        <Link to={ctaTo} className={ctaClass} data-testid={`${testId}-cta`}>
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}
